import { lazy } from 'react';

// Every page in App.js is code-split, so a route render fetches a content-hashed chunk
// from /assets/ (e.g. SquadAnalysis-DvQyu75b.js). A deploy uploads new hashes and — because
// bitbucket-pipelines.yml syncs assets/ with --delete — removes the previous ones, so any
// tab still holding the older index.html asks S3 for a file that no longer exists. The SPA
// fallback answers that 404 with index.html, and the browser refuses it:
//
//   Failed to load module script: Expected a JavaScript-or-Wasm module script but the
//   server responded with a MIME type of "text/html"
//
// The import() then rejects with a TypeError that React surfaces as a render crash — the
// "Something Went Wrong" modal with no failing API behind it, because the page component
// never loaded and so never called anything.
//
// Recovery: retry once (covers an ordinary network blip), then reload the document so the
// browser picks up the current index.html and the hashes that go with it. The reload is
// rate-limited, so if a freshly loaded document still can't fetch the chunk the error falls
// through to the ErrorBoundary instead of looping.

const RELOAD_KEY = 'sk:chunk-reload-at';
const RELOAD_COOLDOWN_MS = 10000;
const RETRY_DELAY_MS = 400;

const CHUNK_ERROR_PATTERN = /dynamically imported module|importing a module script failed|module script failed|expected a javascript/i;

export const isChunkLoadError = (error) => {
    if (!error) return false;
    if (error.name === 'ChunkLoadError') return true;
    return CHUNK_ERROR_PATTERN.test(String(error.message || ''));
};

// Reloads the page, at most once per cooldown window. Returns whether a reload was
// started — callers use that to decide between waiting for the new document and
// surfacing the error.
export const reloadForStaleChunks = () => {
    if (typeof window === 'undefined') return false;

    try {
        const lastReload = Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0;
        // Failing again moments after a reload means the fresh document has the same
        // problem, so another reload would just loop. Let the error surface instead.
        if (Date.now() - lastReload < RELOAD_COOLDOWN_MS) return false;
        window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    } catch {
        // Safari private mode and friends. With nowhere to record the attempt there's no
        // way to guarantee the reload doesn't loop, so don't start one.
        return false;
    }

    window.location.reload();
    return true;
};

// Drop-in replacement for React.lazy for route-level code splitting.
export default function lazyWithRetry(factory) {
    return lazy(() => factory().catch((error) => {
        if (!isChunkLoadError(error)) throw error;

        return new Promise((resolve, reject) => {
            setTimeout(() => {
                factory().then(resolve, (retryError) => {
                    if (isChunkLoadError(retryError) && reloadForStaleChunks()) {
                        // The document is on its way out — never settling keeps React on
                        // the Suspense loader rather than flashing the error modal first.
                        return;
                    }
                    reject(retryError);
                });
            }, RETRY_DELAY_MS);
        });
    }));
}
